// Makes a request to a Devise endpoint that never throws, and copes with a
// response that isn't what the screen expects, so the user always sees a
// message and the screen stays usable (plan item 25).
//
//   const result = await deviseFetch(dispatch, '/user/password.json', options)
//   if (result.failure) {
//     showFailedRequest(dispatch, result)
//     return
//   }
//   const { response, json } = result
//
// `result.failure` is:
//   'network'      the request never got an answer (the server is down, the
//                  network dropped); `response` is null
//   'stale_token'  a 422 whose body isn't `{ errors: {...} }`. A refusal from
//                  our Devise controllers always has that shape, so this is
//                  Rails refusing a missing or stale CSRF token. What it sends
//                  depends on the request: an HTML page (no Accept header), or
//                  a small JSON body such as `{ status: 422, error: '...' }`
//                  (Accept: application/json), which has no `errors`
//   null           anything else. The screen reads `response.status` and `json`
//                  (null if the body wasn't JSON, e.g. a 204 or a 500 page)
//
// Any CSRF token in the response header is stored (Devise replaces the token
// on sign in and sign out). Same for OmniAuth's own, separate request-phase
// CSRF token (X-OmniAuth-CSRF-Token, see ApplicationController#
// set_csrf_token) -- it goes stale the same way after a sign-out, just for
// OpenIdSignIn.jsx's full-page form post instead of a fetch.

import { setAuthToken, setOmniauthToken } from '../../redux/features/user/userSlice'
import { setFlashAlert } from '../../redux/features/ui/uiSlice'
import { formatDeviseErrors } from './deviseErrors'

export const STALE_TOKEN_MESSAGE = 'Your session expired. Please try again.'
export const NETWORK_MESSAGE =
  'Could not reach the server. Check your connection and try again.'

export async function deviseFetch(dispatch, url, options) {
  let response
  try {
    response = await fetch(url, options)
  } catch (error) {
    console.error(error)
    return { response: null, json: null, failure: 'network' }
  }

  // The body may be empty or not JSON, so this must not throw.
  const json = await response.json().catch(() => null)

  const nextCsrfToken = response.headers.get('X-CSRF-Token')
  if (nextCsrfToken) {
    dispatch(setAuthToken(nextCsrfToken))
  }
  const nextOmniauthToken = response.headers.get('X-OmniAuth-CSRF-Token')
  if (nextOmniauthToken) {
    dispatch(setOmniauthToken(nextOmniauthToken))
  }

  const isDeviseRefusal = typeof json?.errors === 'object' && json.errors !== null
  const failure = response.status === 422 && !isDeviseRefusal ? 'stale_token' : null
  return { response, json, failure }
}

// The message for a failed request (a result whose `failure` is set).
export function failureMessage(result) {
  if (result.failure === 'network') {
    return NETWORK_MESSAGE
  }
  if (result.failure === 'stale_token') {
    return STALE_TOKEN_MESSAGE
  }
  return formatDeviseErrors(result.json)
}

// Shows the message for a failed request in the flash. A stale token also gets
// a new one, so the user's next try works.
export function showFailedRequest(dispatch, result) {
  dispatch(setFlashAlert(failureMessage(result)))
  if (result.failure === 'stale_token') {
    refreshCsrfToken(dispatch)
  }
}

// Asks the server for the session's current CSRF tokens (every response
// carries them in the X-CSRF-Token/X-OmniAuth-CSRF-Token headers; GET
// /user/session.json is the cheapest).
export async function refreshCsrfToken(dispatch) {
  try {
    const response = await fetch('/user/session.json', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
    })
    const token = response.headers.get('X-CSRF-Token')
    if (token) {
      dispatch(setAuthToken(token))
    }
    const omniauthToken = response.headers.get('X-OmniAuth-CSRF-Token')
    if (omniauthToken) {
      dispatch(setOmniauthToken(omniauthToken))
    }
  } catch (error) {
    console.error(error)
  }
}
